<template>
  <div>
    <layouts-header />
    <layouts-sidebar />

    <div class="page-wrapper">
      <div class="content">
        <page-header :title="title" />

        <div class="card mb-3">
          <div class="card-header">
            <div class="row">
              <div class="col-md-3">
                <multiselect
                  v-model="filters.employee"
                  :options="employees"
                  track-by="id"
                  label="name"
                  placeholder="Select Employee"
                  @input="onFilterChange"
                  :clearable="true"
                />
              </div>
              <div class="col-md-3">
                <multiselect
                  v-model="filters.exhibition"
                  :options="exhibitions"
                  track-by="id"
                  label="name"
                  placeholder="Select Exhibition"
                  @input="onFilterChange"
                  :clearable="true"
                />
              </div>
              <div class="col-md-3">
                <Datepicker
                  v-model="dateRange"
                  :range="true"
                  :clearable="true"
                  :format="format"
                  placeholder="Select a date range"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="row">
              <div
                class="col-md-3 mb-4"
                v-for="(item, index) in data"
                :key="item.employee_id || index"
                @click="openModal(item)"
                style="cursor: pointer"
              >
                <div class="employee-card text-center">
                  <img
                    :src="item?.profile_picture"
                    alt="User"
                    class="employee-img"
                  />
                  <h5 class="mt-2">{{ item.employee_name }}</h5>
                  <p class="text-muted mb-1">{{ item?.designation }}</p>
                  <p>
                    <strong>Exhibition:</strong>
                    {{ item?.exhibition?.name }}
                    <!-- {{
                      (item.exhibitions || [])
                        .map((ex) => ex.exhibition_id?.name)
                        .filter(Boolean)
                        .join(", ")
                    }} -->
                  </p>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-3">Loading...</div>

            <div
              v-if="!isLoading && data.length === 0"
              class="text-center py-3"
            >
              No results found.
            </div>

            <div
              class="text-center my-3"
              v-if="!isAllLoaded && data.length > 0 && !isLoading"
            >
              <button
                class="btn btn-primary"
                @click="loadMore"
                :disabled="isLoading"
              >
                Load More
              </button>
            </div>
          </div>
        </div>

        <employee-leads-modal
          v-if="isShowModal"
          :employee="selectedEmployee"
          @close="isShowModal = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/utils/http";
import Multiselect from "vue-multiselect";
import employeeLeadsModal from "./employee-leads-modal.vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  components: {
    employeeLeadsModal,
    Multiselect,
    Datepicker,
  },
  data() {
    return {
      title: "Employee Activity Report",
      data: [],
      isLoading: false,
      isShowModal: false,
      selectedEmployee: null,
      employees: [],
      exhibitions: [],
      dateRange: "",
      start_date: null,
      end_date: null,
      format: "yyyy-MM-dd",
      filters: {
        employee: null,
        exhibition: null,
      },
      currentPage: 1,
      perPage: 12,
      total: 0,
      isAllLoaded: false,
    };
  },
  mounted() {
    // this.dateRange = [new Date(this.start_date), new Date(this.end_date)];
    this.fetchEmployees();
    this.fetchExhibitions();
    this.loadData();
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.isAllLoaded = false;
        this.isLoading = false;
        this.data = [];
        this.loadData();
      },
    },
    dateRange(newRange) {
      if (newRange) {
        this.start_date = this.formatDate(newRange[0]); // Set formatted start date
        this.end_date = this.formatDate(newRange[1]);
        console.log("start date", this.start_date); // Set formatted end date
        this.currentPage = 1;
        this.isAllLoaded = false;
        this.isLoading = false;
        this.data = [];
        this.loadData();
      } else {
        this.start_date = null;
        this.end_date = null;
      }
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return null;
      // Using toLocaleString to return YYYY-MM-DD HH:mm:ss
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return new Date(date).toLocaleString("sv-SE", options).replace("T", " ");
    },
    async loadData(page = 1) {
      if (this.isLoading || this.isAllLoaded) return;

      this.isLoading = true;

      try {
        const params = {
          page,
          per_page: this.perPage,
        };

        if (this.filters.employee) {
          params.employee_id = this.filters.employee.id;
          console.log("employee id", this.filters.employee.id);
        }
        if (this.filters.exhibition) {
          params.exhibition_id = this.filters.exhibition.id;
          console.log("exhibition id", this.filters.exhibition.id);
        }

        if (this.start_date && this.end_date) {
          params.start_date = this.start_date;
          params.end_date = this.end_date;
        }

        const response = await http.get("employee-activity-report", { params });
        const resData = response.data.contents;

        // Normalize employees data
        const employees = resData.data.map((employee) => ({
          ...employee,
          organization_name: employee.leads?.[0]?.organization_name || "",
        }));

        if (page === 1) {
          this.data = employees;
        } else {
          this.data = [...this.data, ...employees];
        }

        this.total = resData.total;
        this.currentPage = resData.current_page;
        this.isAllLoaded = this.data.length >= this.total;
      } catch (error) {
        console.error("Failed to load employee activity:", error);
      } finally {
        this.isLoading = false;
      }
    },

    loadMore() {
      if (!this.isAllLoaded && !this.isLoading) {
        this.currentPage++;
        this.loadData(this.currentPage);
      }
    },

    onFilterChange() {
      // Reset pagination & data when filters change
      this.currentPage = 1;
      this.isAllLoaded = false;
      this.data = [];
      this.loadData();
    },

    async fetchEmployees() {
      try {
        const response = await http.get("/employees");
        this.employees = response.data.contents.map((emp) => ({
          id: emp.id,
          name: emp.user?.name || "Unknown",
        }));
      } catch (e) {
        console.error("Failed to fetch employees", e);
      }
    },

    async fetchExhibitions() {
      try {
        const response = await http.get("/exhibitions");
        this.exhibitions = response.data.contents.map((ex) => ({
          id: ex.id,
          name: ex.name,
        }));
      } catch (e) {
        console.error("Failed to fetch exhibitions", e);
      }
    },

    openModal(employee) {
      this.selectedEmployee = employee;
      this.isShowModal = true;
    },
  },
};
</script>

<style scoped>
.employee-card {
  border: 1px solid #ddd;
  padding: 15px;
  border-radius: 6px;
  transition: box-shadow 0.2s ease;
}
.employee-card:hover {
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
}
.employee-img {
  border-radius: 50%;
  width: 80px;
  height: 80px;
  object-fit: cover;
}
</style>
